import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import InfiniteLoader from 'react-virtualized/dist/commonjs/InfiniteLoader'
import List, { ListRowProps } from 'react-virtualized/dist/commonjs/List'
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer'
import { UnscheduledTasksFormName, UnscheduledTasksFormValues, useUnscheduledTasks } from '../context/UnscheduledTasksContext'
import { getStyles } from 'isotope-client'
import { compose } from 'redux'
import { withStyles } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { FormattedMessage, useIntl } from 'react-intl'
import { Index } from 'react-virtualized'
import Input from '../../../../components/form/Input'
import { Field, FormAction, InjectedFormProps, reduxForm, submit } from 'redux-form'
import IconButton from '../../../../components/IconButton'
import SearchIcon from '@material-ui/icons/Search'
import { connect } from 'react-redux'
import { ConfigProps } from 'redux-form/lib/reduxForm'
import Loader from '../../../../components/layout/Loader'
import NoResultPage from '../../../../components/NoResultPage'
import { getLoadedValueOrElse } from '../../../../utils/loadingValue'
import TaskCard from './planning/TaskCard'
import { ScheduledTask, TaskPanelRedirectionParameters, UnscheduledTask } from '../services/schedule.types'
import { Draggable, Dropcontainer } from '@mobiscroll/react'
import { LocalDateTime } from '../../../../utils/date/local-date-time'
import { DateFormat, TimeFormat, TimeUnit } from '../../../../utils/date/model/date'
import classnames from 'classnames'
import { useScheduler } from '../context/SchedulerContext'
import { useSnackbar } from '../../../../components/layout/snackbar/SnackbarContext'
import { desaffecterUsers } from '../../gestionTache/gestionTachesApi'

/**
 * Styles
 */
const styles = () => getStyles({
	container: {
		flex: '1 1 auto'
	},
	errorContainer: {
		justifyContent: 'center',
		alignItems: 'center',
		flex: 1,
		display: 'flex'
	},
	loaderContainer: {
		alignContent: 'center'
	},
	formContainer: {
		display: 'flex',
		flexDirection: 'row' as 'row',
		alignItems: 'center'
	},
	searchButton: {
		width: 40,
		height: 40
	},
	inputContainer: {
		flex: 1,
		paddingRight: 0,
		'& input': {
			paddingTop: 12
		}
	},
	loadingContainer: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center'
	},
	cell: {
		borderRadius: 10,
		padding: '12px',
		gap: '4px',
		cursor: 'pointer'
	},
	cellTitle: {
		fontStyle: 'normal',
		lineHeight: '18px',
		fontSize: 14,
		fontWeight: 600
	},
	dropZone: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column' as 'column',
		minHeight: 0
	},
	insertionLine: {
		position: 'absolute' as 'absolute',
		left: 0,
		right: 0,
		height: 3,
		backgroundColor: 'lightgrey',
		borderRadius: 2,
		zIndex: 2,
		pointerEvents: 'none' as 'none'
	}
})

type UnscheduledTaskListProps = {
	isResultat: boolean
	classes: ClassNameMap,
	submit: (form: string) => FormAction
	setTaskPanelRedirectionParameters: Dispatch<SetStateAction<TaskPanelRedirectionParameters | undefined>>
}

type UnscheduledTaskItemProps = {
	task: UnscheduledTask
	canDrag: boolean
	highlighted?: boolean
}

const UnscheduledTaskItem: FC<UnscheduledTaskItemProps> = ({ task, canDrag, highlighted = false }) => {
	const [draggable, setDraggable] = useState<HTMLDivElement | undefined>(undefined)

	const setDragElm = useCallback((elm: HTMLDivElement) => {
		setDraggable(elm)
	}, [])

	// Pour l'affichage dans le calendar, on fausse un start/end
	const start = LocalDateTime.now()
	const end = start.plus(Math.ceil(Number(task.duree) / 5) * 5, TimeUnit.minute)

	return (
		<div ref={setDragElm}>
			<TaskCard task={task} id={task.id} fromUnscheduled highlighted={highlighted} />
			{canDrag && <Draggable
				dragData={{
					...task,
					start: start.time.format(TimeFormat.TIME_24_SIMPLE),
					end: end.time.format(TimeFormat.TIME_24_SIMPLE)
				}}
				element={draggable}
			/>}
		</div>
	)
}

const UnscheduledTaskList: FC<InjectedFormProps<UnscheduledTasksFormValues, UnscheduledTaskListProps> & UnscheduledTaskListProps> = ({ handleSubmit, classes, submit, setTaskPanelRedirectionParameters, isResultat }) => {
	const { formatMessage } = useIntl()
	const { unscheduledTasks, nbUnscheduledTasks, loadMoreUnscheduledTasks, insertUnscheduledTask, highlightedTaskId } = useUnscheduledTasks()
	const { tasks, viewInfos, selectedDate, removeScheduledTask } = useScheduler()
	const { snackSuccess, snackError } = useSnackbar()
	const [dropElement, setDropElement] = useState<HTMLDivElement | null>(null)
	const [isDragOver, setIsDragOver] = useState(false)
	// Index de la ligne survolée pendant le drag (position d'insertion), pour afficher le trait d'insertion
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

	const showDetail = useCallback(
		(task: UnscheduledTask) => setTaskPanelRedirectionParameters({
			id: task.id,
			unplanned: true
		}),
		[setTaskPanelRedirectionParameters]
	)

	const rowCacheRef = useRef(
		new CellMeasurerCache({
			defaultHeight: 52,
			fixedWidth: true
		})
	)
	const unscheduledTasksValue = getLoadedValueOrElse(unscheduledTasks, [])
	const myListRef = useRef<List | null>()

	const prevUnscheduledTasksLength = useRef(unscheduledTasksValue.length)

	// Retrouve l'index de la ligne située sous un point (data-index posé sur chaque ligne) ; défaut = fin de liste
	const computeIndexFromPoint = (clientX?: number, clientY?: number): number => {
		if (clientX == null || clientY == null) {
			return unscheduledTasksValue.length
		}
		const row = document.elementFromPoint(clientX, clientY)?.closest('[data-index]')
		const parsed = row ? Number(row.getAttribute('data-index')) : NaN
		return Number.isNaN(parsed) ? unscheduledTasksValue.length : parsed
	}

	const computeDropIndex = (domEvent: any): number =>
		computeIndexFromPoint(
			domEvent?.clientX ?? domEvent?.changedTouches?.[0]?.clientX,
			domEvent?.clientY ?? domEvent?.changedTouches?.[0]?.clientY
		)

	// Pendant le drag au-dessus de la liste, on suit le pointeur (mobiscroll ne fournit pas d'event de déplacement)
	// pour connaître en continu la position d'insertion et afficher le trait
	useEffect(() => {
		if (!isDragOver) {
			return
		}
		const handleMove = (event: PointerEvent) => setDragOverIndex(computeIndexFromPoint(event.clientX, event.clientY))
		document.addEventListener('pointermove', handleMove)
		return () => document.removeEventListener('pointermove', handleMove)
	}, [isDragOver, unscheduledTasksValue.length])

	// Drop d'un event du calendrier sur la liste : on désassigne les personnes -> la tâche redevient non planifiée
	const handleItemDrop = (args: any) => {
		// Position d'insertion = celle indiquée par le trait (suivi pointeur), avec repli sur les coordonnées du drop
		const dropIndex = dragOverIndex != null ? dragOverIndex : computeDropIndex(args?.domEvent)
		setIsDragOver(false)
		setDragOverIndex(null)
		const droppedId = `${args?.data?.id ?? ''}`
		// On ne traite que les events provenant du calendrier (tâches planifiées connues)
		const scheduledTask = tasks.find(t => `${t.id}` === droppedId)
		if (!scheduledTask) {
			return
		}
		const users = (scheduledTask.resource || [])
			.map(code => viewInfos.ressources.find(r => `${r.id}` === `${code}`)?.userId)
			.filter((userId): userId is string => !!userId)
		if (users.length === 0) {
			return
		}
		desaffecterUsers({ users, taches: [droppedId] }, selectedDate.format(DateFormat.SHORT_DATE_WITH_DASH))
			.then(() => {
				removeScheduledTask(droppedId)
				const { start, end, ...rest } = scheduledTask
				insertUnscheduledTask({ ...rest, planned: false } as UnscheduledTask, dropIndex)
				snackSuccess({ id: 'schedule.unscheduledTasks.snack.deplanifiee' })
			})
			.catch(() => snackError({ id: 'schedule.unscheduledTasks.snack.deplanifieeError' }))
	}

	const isTaskLoaded = (params: Index) => params.index < unscheduledTasksValue.length

	const unscheduledTaskRendered = ({ index, key, style, parent }: ListRowProps) => {
		let content
		if (!isTaskLoaded({ index })) {
			content = (
				<div data-index={index} style={{ ...style, paddingBottom: '6px', display: 'flex', justifyContent: 'center' }}>
					<CircularProgress size={20} />
				</div>
			)
		} else {
			const task = unscheduledTasksValue[index]
			const showInsertLine = dragOverIndex === index

			content = <div
				data-index={index}
				style={{ ...style, paddingBottom: '6px' }}
				onClick={() => showDetail(task)}
			>
				{showInsertLine && <div className={classes.insertionLine} style={{ top: 0 }} />}
				<UnscheduledTaskItem task={task} canDrag={!isResultat} highlighted={highlightedTaskId === task.id} />
			</div>

		}

		return (
			<CellMeasurer
				cache={rowCacheRef.current}
				columnIndex={0}
				key={key}
				parent={parent}
				rowIndex={index}
			>
				{content}
			</CellMeasurer>
		)
	}

	const reSizeRowRender = (index: number) => {
		// Clear cache for the row at index and column 0
		rowCacheRef.current.clear(index, 0)
		if (myListRef.current) {
			myListRef.current.recomputeRowHeights(index)
		}
	}

	const renderBody = () => {
		if (unscheduledTasks.loading) {
			return <div className={classes.loaderContainer}><Loader fromBo={false} /></div>
		}

		if (unscheduledTasks.error) {
			return (
				<div className={classes.errorContainer}>
					<FormattedMessage id="schedule.error.loading" />
				</div>
			)
		}

		if (nbUnscheduledTasks === 0) {
			return <NoResultPage emptyStateMessageId="schedule.unscheduledTasks.empty" />
		}

		return (
			<div className={classes.container}>
				<InfiniteLoader
					isRowLoaded={isTaskLoaded}
					rowCount={nbUnscheduledTasks}
					loadMoreRows={loadMoreUnscheduledTasks}
					threshold={2}
					minimumBatchSize={1}
				>
					{({ onRowsRendered, registerChild }) => (
						<AutoSizer disableWidth>
							{({ height }) => {
								return (
									<List
										ref={(listRef) => {
											myListRef.current = listRef
											registerChild(listRef)
										}}
										onRowsRendered={onRowsRendered}
										rowRenderer={unscheduledTaskRendered}
										height={height}
										rowCount={unscheduledTasksValue.length}
										width={350}
										estimatedRowSize={52}
										rowHeight={rowCacheRef.current.rowHeight}
										deferredMeasurementCache={rowCacheRef.current}
										overscanRowCount={1}
									/>

								)
							}}
						</AutoSizer>
					)}
				</InfiniteLoader>
			</div>
		)
	}

	useEffect(() => {
		if (prevUnscheduledTasksLength.current !== unscheduledTasksValue.length) {
			reSizeRowRender(prevUnscheduledTasksLength.current)
			prevUnscheduledTasksLength.current = unscheduledTasksValue.length
		}
	}, [unscheduledTasksValue])

	return (
		<>
			<form onSubmit={handleSubmit} className={classes.formContainer}>
				<Field
					name="query"
					type="text"
					component={Input}
					containerClassName={classes.inputContainer}
					placeholder={formatMessage({ id: 'schedule.unscheduledTasks.searchPlaceHolder' })}
				/>
				<IconButton
					tooltip={undefined}
					tooltipProps={undefined}
					onClick={() => submit(UnscheduledTasksFormName)}
					className={classes.searchButton}
				>
					<SearchIcon color="primary" />
				</IconButton>
			</form>
			<div
				ref={setDropElement}
				className={classnames(classes.dropZone, { [classes.dropZoneActive]: isDragOver })}
			>
				{renderBody()}
			</div>
			{!isResultat && dropElement && (
				<Dropcontainer
					element={dropElement}
					onItemDrop={handleItemDrop}
					onItemDragEnter={(args: any) => {
						// N'activer l'indicateur d'insertion que pour un drag provenant du calendrier (tâche planifiée),
						// pas pour le drag d'une carte de la liste elle-même
						const isFromCalendar = tasks.some(t => `${t.id}` === `${args?.data?.id ?? ''}`)
						if (isFromCalendar) {
							setIsDragOver(true)
						}
					}}
					onItemDragLeave={() => {
						setIsDragOver(false)
						setDragOverIndex(null)
					}}
				/>
			)}
		</>
	)
}

const actions = {
	submit
}

type UnscheduledTaskListParentProps =
	ConfigProps<UnscheduledTasksFormValues, UnscheduledTaskListProps> & UnscheduledTasksFormValues
	& { isResultat: boolean, setTaskPanelRedirectionParameters: Dispatch<SetStateAction<TaskPanelRedirectionParameters | undefined>> }

export default compose<FC<UnscheduledTaskListParentProps>>(
	connect(undefined, actions),
	reduxForm({
		form: UnscheduledTasksFormName,
		enableReinitialize: true
	}),
	withStyles(styles)
)(UnscheduledTaskList)
